// itemNames: item id -> English name, looked up per id from the funnyresources
// API (`/api/item/{id}` -> { id, name }). Results are cached in localStorage
// and invalidated when the funnyresources data version changes.
import { ref } from 'vue';
import { resourceBaseUrl } from '@/util';

const CACHE_KEY = 'funnycharts.itemNames.v1';
const CONCURRENCY = 8;
// Fetched names land in the reactive map in batches, so a 1000-id lookup
// re-renders a table a handful of times instead of once per id.
const FLUSH_MS = 250;

// id -> name, or null when funnyresources has no entry for the id.
const names = ref<Record<number, string | null>>({});
let cacheVersion = '';
const inFlight = new Set<number>();
let pending: Record<number, string | null> = {};
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function loadCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw) as { version?: string; names?: Record<number, string | null> };
        if (parsed && typeof parsed.version === 'string' && parsed.names && typeof parsed.names === 'object') {
            cacheVersion = parsed.version;
            names.value = parsed.names;
        }
    } catch { /* unreadable cache: start empty */ }
}

function saveCache() {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ version: cacheVersion, names: names.value }));
    } catch { /* storage unavailable or full: the in-memory map still works */ }
}

loadCache();

// The version check runs once per page load; until it resolves, cached
// names are used as-is.
const versionChecked: Promise<void> = (async () => {
    try {
        const r = await fetch(`${resourceBaseUrl}/api/version.json`);
        if (!r.ok) return;
        const { version } = await r.json() as { version?: unknown };
        const v = String(version ?? '');
        if (v && v !== cacheVersion) {
            cacheVersion = v;
            names.value = {};
            saveCache();
        }
    } catch { /* offline: keep the cache */ }
})();

function flush() {
    flushTimer = null;
    names.value = { ...names.value, ...pending };
    pending = {};
    saveCache();
}

function record(id: number, name: string | null) {
    pending[id] = name;
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
}

async function fetchOne(id: number) {
    try {
        const r = await fetch(`${resourceBaseUrl}/api/item/${id}`);
        if (r.status === 404) {
            record(id, null);
            return;
        }
        if (!r.ok) return; // transient: retried on the next ensureItemNames
        const data = await r.json() as { name?: unknown };
        record(id, typeof data.name === 'string' && data.name ? data.name : null);
    } catch { /* network error: retried on the next ensureItemNames */ }
}

// ensureItemNames fetches every id not yet known, CONCURRENCY at a time.
export async function ensureItemNames(ids: Iterable<number>): Promise<void> {
    await versionChecked;
    const todo = [...new Set(ids)].filter(id =>
        id > 0 && !(id in names.value) && !(id in pending) && !inFlight.has(id));
    if (!todo.length) return;
    todo.forEach(id => inFlight.add(id));
    let next = 0;
    const worker = async () => {
        while (next < todo.length) {
            const id = todo[next++];
            await fetchOne(id);
            inFlight.delete(id);
        }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker));
    if (flushTimer) {
        clearTimeout(flushTimer);
        flush();
    }
}

// itemName returns the English name, or "Item <id>" while unknown. Reading it
// inside a computed re-evaluates that computed as names arrive.
export function itemName(id: number): string {
    return names.value[id] ?? `Item ${id}`;
}
